import React, { useCallback, useEffect, useRef, useState, MouseEvent } from 'react';
import styled from 'styled-components';
import { CommonLayer, Flex } from '@lemonade-hq/cdk';
import { motion, useScroll } from 'motion/react';
import { lerp } from '@lemonade-hq/ts-helpers';
import { TextReview, VideoReview } from '../types';
import { Player } from './Player';

const StyledBackdrop = styled(motion.create(Flex))<{ visible: boolean }>`
  position: fixed;
  width: 100vw;
  height: 100dvh;
  top: 0;
  left: 0;
  z-index: ${CommonLayer.DialogBackground};
  background-color: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(4px);
  overflow-y: auto;
  flex-direction: column;
  align-items: center;
  padding-block: calc(calc(100vh - min(800px, 82vh)) / 2);
  opacity: ${({ visible }) => (visible ? 1 : 0)};
  z-index: ${({ visible }) => (visible ? CommonLayer.DialogBackground : -1)};
  transition:
    opacity 0.3s ease-in-out,
    z-index 0s ${({ visible }) => (visible ? 0 : 0.3)}s ease-in-out;
`;

const scrollToReview = (reviewId: number, instant = false): void => {
  const activeReviewElement = document.getElementById(`review-${reviewId}`);

  activeReviewElement?.scrollIntoView({
    behavior: instant ? 'instant' : 'smooth',
    block: 'center',
  });
};

interface PlayerDialogProps {
  initialReview: number | null;
  reviews: (VideoReview | TextReview)[];
  videosDisclosure?: string;
  onClose: () => void;
}

export const PlayerDialog: React.FC<PlayerDialogProps> = ({ initialReview, reviews, videosDisclosure, onClose }) => {
  const [activeReview, setActiveReview] = useState<number | null>(null);
  const [lockScrolling, setLockScrolling] = useState(false);
  const backdropRef = useRef<HTMLDivElement>(null);
  const { scrollYProgress } = useScroll({ container: backdropRef });
  const [isMuted, setIsMuted] = useState(false);
  const [captionsOn, setCaptionsOn] = useState(false);

  useEffect(() => {
    let timeout: NodeJS.Timeout;
    setActiveReview(initialReview);
    if (!initialReview) return;
    scrollToReview(initialReview, true);
    const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
    const bodyStyle = document.querySelector('body')?.style;
    if (bodyStyle) {
      bodyStyle.setProperty('overflow', 'hidden');
      bodyStyle.setProperty('height', '100%');
      bodyStyle.setProperty('padding-right', `${scrollbarWidth}px`);
    }

    const handleResize = (): void => {
      setLockScrolling(true);

      timeout = setTimeout(() => {
        setLockScrolling(false);
      }, 200);
    };

    window.addEventListener('resize', handleResize);

    return () => {
      if (bodyStyle) {
        bodyStyle.removeProperty('overflow');
        bodyStyle.removeProperty('height');
        bodyStyle.removeProperty('padding-right');
      }
      window.removeEventListener('resize', handleResize);
      clearTimeout(timeout);
    };
  }, [initialReview]);

  useEffect(() => {
    if (activeReview == null) return;

    scrollToReview(activeReview);
  }, [activeReview]);

  useEffect(() => {
    let timeout: NodeJS.Timeout;

    const handleChange = (latest: number): void => {
      clearTimeout(timeout);

      timeout = setTimeout(() => {
        const currentIndex = Math.round(lerp(0, reviews.length - 1, latest));

        if (currentIndex >= 0 && currentIndex < reviews.length) {
          const currentReviewId = reviews[currentIndex].id;

          if (!currentReviewId) return;

          setActiveReview(currentReviewId);
        }
      }, 100);
    };

    const unsubscribe = scrollYProgress.on('change', handleChange);

    return () => {
      unsubscribe();
      clearTimeout(timeout);
    };
  }, [scrollYProgress, reviews]);

  const changeActiveReview = useCallback(
    (reviewId: number): void => {
      if (activeReview === reviewId) return;

      scrollToReview(reviewId);
      setActiveReview(reviewId);
    },
    [activeReview],
  );

  const isVisible = initialReview != null;

  const handleClick = useCallback(
    (e: MouseEvent<HTMLDivElement>) => {
      if (e.target === backdropRef.current) onClose();
    },
    [onClose],
  );

  return (
    <StyledBackdrop
      onClick={handleClick}
      id="player-backdrop"
      ref={backdropRef}
      style={{
        scrollSnapType: lockScrolling ? 'y mandatory' : undefined,
        '--t': scrollYProgress,
        '--num-of-items': reviews.length,
        '--min-scale': 0.9,
      }}
      visible={isVisible}
    >
      {reviews.map((review, index) => (
        <Player
          key={review.id}
          index={index}
          review={review}
          onClose={onClose}
          changeActiveReview={changeActiveReview}
          isActive={review.id === activeReview}
          isMuted={isMuted}
          setIsMuted={setIsMuted}
          captionsOn={captionsOn}
          setCaptionsOn={setCaptionsOn}
          videosDisclosure={videosDisclosure}
        />
      ))}
    </StyledBackdrop>
  );
};
